import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/ic-exit-rounded-purple.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { isPbmConfirmDisabled } from '../pbm/pbmHelpers';

interface PbmConsentStepProps {
  programName: string;
  optoutUrl?: string;
  partnerProgramUrl?: string;
  controllerName?: string;
  consentGiven: boolean;
  onConsentChange: (checked: boolean) => void;
  isLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const PbmConsentStep: React.FC<PbmConsentStepProps> = ({
  programName,
  optoutUrl = '',
  partnerProgramUrl = '',
  controllerName = '',
  consentGiven,
  onConsentChange,
  isLoading = false,
  onConfirm,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isConfirmDisabled = isPbmConfirmDisabled(consentGiven, isLoading);
  const displayProgramName = programName || '[Nome do Programa]';
  const displayControllerName = controllerName || '[Controladora]';

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
        {isMobile ? (
          <>
            <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
              <LeftArrowIcon onClick={onClose} cursor={'pointer'} />
              <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
                Voltar
              </Text>
            </Flex>
            <Flex flexDirection={'column'} padding={'0px 20px'} mb={'16px'}>
              <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
                Programa de Benefício em Medicamentos
              </Text>
            </Flex>
          </>
        ) : (
          <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} padding={'20px'}>
            <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
              Programa de Benefício em Medicamentos
            </Text>
            <CloseIcon onClick={onClose} cursor={'pointer'} />
          </Flex>
        )}
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Flex flexDirection={'column'} gap={'1px'}>
          <Text fontSize={'18px'} fontWeight={700} color={'primary.main'}>
            Confirmação de consentimento
          </Text>

          <Text fontSize={'14px'} color={'#343A40'} lineHeight={'17px'} paddingBottom={'18px'}>
            Para continuar, registre o consentimento do paciente para o cadastro no programa:
          </Text>

          <Flex flexDirection={'column'} gap={'16px'}>
            <Checkbox
              isChecked={consentGiven}
              onChange={(e) => onConsentChange(e.target.checked)}
              size="md"
              alignItems={'flex-start'}
            >
              <Box ml={'8px'} flex={1}>
                <Text fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                  Confirmo que obtive o consentimento do paciente para cadastrá-lo no{' '}
                  <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                    {displayProgramName}
                  </Text>
                  , que dá desconto na compra do medicamento prescrito, tendo-o informado de que:
                </Text>
                <Box as="ul" pl={'20px'} mt={'8px'} mb={0} listStyleType={'disc'} listStylePosition={'outside'}>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    o desconto só é aplicado em farmácias participantes do programa;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    o programa é operado pela {displayControllerName}, que define, junto com a Mevo, como os dados do
                    cadastro são tratados, sendo ambas corresponsáveis por eles;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    seus dados de nome, CPF, telefone e e-mail serão compartilhados com a {displayControllerName} para
                    viabilizar o cadastro e a aplicação do desconto;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    o cadastro é opcional e não altera a emissão da receita nem o tratamento;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                    pode revogar o consentimento a qualquer momento, de forma simples e gratuita,{' '}
                    {optoutUrl ? (
                      <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                        <a href={optoutUrl} target="_blank" rel="noopener noreferrer">
                          no portal do Programa
                        </a>
                      </Text>
                    ) : (
                      '[inserir url]'
                    )}
                    .
                  </Text>
                </Box>
              </Box>
            </Checkbox>
          </Flex>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'0px 20px 20px 20px'}>
        <Box padding={'16px'} backgroundColor={'#EEF1FF'} borderRadius={'10px'}>
          <Flex flexDirection={'row'} gap={'12px'}>
            <Box flexShrink={0}>
              <InfoIcon width={'16px'} height={'16px'} fill="#5C2E8C" />
            </Box>
            <Flex flexDirection={'column'} gap={'4px'}>
              <Text fontSize={'14px'} color={'#343A40'}>
                O Programa de Benefício em Medicamentos (PBM) é operado pela {displayControllerName} e apoia o acesso ao
                tratamento com desconto na compra do medicamento. O desconto só é aplicado em farmácias participantes.{' '}
                <Text
                  as="span"
                  color={'#343A40'}
                  fontWeight={700}
                  textDecoration={'underline'}
                  cursor={'pointer'}
                >
                  <a
                    href={partnerProgramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'inherit', cursor: 'inherit' }}
                  >
                    Entenda como funciona
                  </a>
                </Text>
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onClose}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Cancelar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={isConfirmDisabled}
            isLoading={isLoading}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
          >
            Continuar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default PbmConsentStep;
